import React, { useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import {
	ArrowLeft,
	CheckCircle,
	Clock,
	Copy,
	RefreshCw,
	XCircle,
} from 'lucide-react';

import {
	useCheckCertificateAndUpdateCloudFrontMutation,
	useGetCertificateValidationInstructionsQuery,
	useGetTenantDomainsQuery,
} from '../api/domains';
import { useGetTenantQuery, useUpdateTenantMutation } from '../api/tenants';
import {
	Alert,
	Button,
	PageContent,
	PageHeader,
	Typography,
} from '../components/common';
import Loader from '../components/Loader';
import { useToast } from '../context/ToastContext';
import { FEATURES, hasFeature, withFeature } from '../hooks/useFeature';

import type { Feature } from '../hooks/useFeature';

const TenantDetailsPage: React.FC = () => {
	const { tenantId } = useParams<{ tenantId: string }>();
	const navigate = useNavigate();
	const { showToast } = useToast();
	const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);

	const {
		data: tenantData,
		isLoading: tenantLoading,
		isFetching: tenantFetching,
		error: tenantError,
	} = useGetTenantQuery(tenantId || '', {
		skip: !tenantId,
	});
	const {
		data: domainsData,
		isLoading: domainsLoading,
		refetch: refetchDomains,
	} = useGetTenantDomainsQuery(tenantId || '', {
		skip: !tenantId,
	});

	const { data: certInstructions, isLoading: certLoading } =
		useGetCertificateValidationInstructionsQuery(selectedDomainId || '', {
			skip: !selectedDomainId,
		});

	const [checkCertificate, { isLoading: checkingCertificate }] =
		useCheckCertificateAndUpdateCloudFrontMutation();
	const [updateTenant, { isLoading: savingFeature }] =
		useUpdateTenantMutation();

	const tenant = tenantData?.tenant;
	const domains = domainsData?.domains || [];

	const handleToggleFeature = async (feature: Feature, enabled: boolean) => {
		if (!tenant) return;
		try {
			await updateTenant({
				tenantId: tenant.id,
				data: { metadata: withFeature(tenant.metadata, feature, enabled) },
			}).unwrap();
			showToast(
				'Feature flags',
				`${FEATURES[feature]} ${enabled ? 'enabled' : 'disabled'}`,
				'success',
			);
		} catch {
			showToast(
				'Feature flags',
				`Could not update ${FEATURES[feature]}`,
				'error',
			);
		}
	};

	const handleViewCertificateInstructions = (domainId: string) => {
		setSelectedDomainId(domainId);
	};

	const handleCheckCertificateAndUpdate = async (domainId: string) => {
		try {
			const result = await checkCertificate(domainId).unwrap();
			if (result.success) {
				showToast(result.message, 'success');
				refetchDomains();
			} else {
				showToast(result.message, 'error');
			}
		} catch (error: any) {
			showToast(
				error?.data?.message ||
					'Failed to check certificate and update CloudFront',
				'error',
			);
		}
	};

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
		showToast('Copied to clipboard', 'success');
	};

	const getCertificateStatusBadge = (status?: string) => {
		if (!status) return null;

		const statusMap: Record<
			string,
			{ label: string; color: string; icon: React.ReactNode }
		> = {
			ISSUED: {
				label: 'Issued',
				color: 'bg-green-100 text-green-800',
				icon: <CheckCircle className="w-4 h-4" />,
			},
			PENDING_VALIDATION: {
				label: 'Pending Validation',
				color: 'bg-yellow-100 text-yellow-800',
				icon: <Clock className="w-4 h-4" />,
			},
			VALIDATION_TIMED_OUT: {
				label: 'Validation Timed Out',
				color: 'bg-red-100 text-red-800',
				icon: <XCircle className="w-4 h-4" />,
			},
			FAILED: {
				label: 'Failed',
				color: 'bg-red-100 text-red-800',
				icon: <XCircle className="w-4 h-4" />,
			},
		};

		const statusInfo = statusMap[status] || {
			label: status,
			color: 'bg-gray-100 text-gray-800',
			icon: <Clock className="w-4 h-4" />,
		};

		return (
			<span
				className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}
			>
				{statusInfo.icon}
				{statusInfo.label}
			</span>
		);
	};

	const getCloudFrontStatusBadge = (status: string) => {
		const statusMap: Record<string, { label: string; color: string }> = {
			active: { label: 'Active', color: 'bg-green-100 text-green-800' },
			provisioning: {
				label: 'Provisioning',
				color: 'bg-yellow-100 text-yellow-800',
			},
			pending: { label: 'Pending', color: 'bg-gray-100 text-gray-800' },
			failed: { label: 'Failed', color: 'bg-red-100 text-red-800' },
		};

		const statusInfo = statusMap[status] || {
			label: status,
			color: 'bg-gray-100 text-gray-800',
		};

		return (
			<span
				className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}
			>
				{statusInfo.label}
			</span>
		);
	};

	if (tenantLoading || domainsLoading) {
		return (
			<div className="min-h-screen bg-gray-50">
				<PageHeader title="Tenant Details" />
				<PageContent>
					<Loader size="lg" text="Loading tenant details..." />
				</PageContent>
			</div>
		);
	}

	if (tenantError || (!tenantLoading && !tenant)) {
		return (
			<div className="min-h-screen bg-gray-50">
				<PageHeader title="Tenant Details" />
				<PageContent>
					<Alert variant="error">Tenant not found</Alert>
					<Button
						onClick={() => navigate('/tenants')}
						variant="outline"
						icon={ArrowLeft}
						className="mt-4"
					>
						Back to Tenants
					</Button>
				</PageContent>
			</div>
		);
	}

	const customDomains = domains.filter((d) => d.domain_type === 'custom');

	return (
		<div className="min-h-screen bg-gray-50">
			<PageHeader
				title="Tenant Details"
				actions={
					<Button
						onClick={() => navigate('/tenants')}
						variant="outline"
						icon={ArrowLeft}
					>
						Back to Tenants
					</Button>
				}
			/>
			<PageContent>
				{/* Tenant Information */}
				<div className="bg-white rounded-lg shadow-sm p-6 mb-6">
					<Typography variant="h2" className="mb-4">
						Tenant Information
					</Typography>
					<div className="grid grid-cols-2 gap-4">
						<div>
							<p className="text-sm font-medium text-gray-500">Name</p>
							<p className="text-sm text-gray-900">{tenant.name}</p>
						</div>
						<div>
							<p className="text-sm font-medium text-gray-500">Subdomain</p>
							<p className="text-sm text-gray-900">{tenant.subdomain}</p>
						</div>
						<div>
							<p className="text-sm font-medium text-gray-500">Company Email</p>
							<p className="text-sm text-gray-900">{tenant.company_email}</p>
						</div>
						<div>
							<p className="text-sm font-medium text-gray-500">Plan</p>
							<p className="text-sm text-gray-900 capitalize">{tenant.plan}</p>
						</div>
						<div>
							<p className="text-sm font-medium text-gray-500">Status</p>
							<p className="text-sm text-gray-900 capitalize">
								{tenant.status}
							</p>
						</div>
						<div>
							<p className="text-sm font-medium text-gray-500">Created At</p>
							<p className="text-sm text-gray-900">
								{new Date(tenant.created_at).toLocaleString()}
							</p>
						</div>
					</div>
				</div>

				{/* Feature flags */}
				<div className="bg-white rounded-lg shadow-sm p-6 mb-6">
					<Typography variant="h2" className="mb-1">
						Feature flags
					</Typography>
					<p className="text-sm text-gray-500 mb-4">
						Modules shown only to this tenant. Off by default.
					</p>
					<div className="space-y-3">
						{(Object.keys(FEATURES) as Feature[]).map((feature) => (
							<label
								key={feature}
								className="flex items-center gap-3 text-sm text-gray-900"
							>
								<input
									type="checkbox"
									className="h-4 w-4 rounded border-gray-300 text-purple-600"
									checked={hasFeature(tenant ?? null, feature)}
									// Wait for the refetch too: the next toggle is built from tenant.metadata.
									disabled={savingFeature || tenantFetching}
									onChange={(e) =>
										handleToggleFeature(feature, e.target.checked)
									}
								/>
								{FEATURES[feature]}
							</label>
						))}
					</div>
				</div>

				{/* Domains Section */}
				<div className="bg-white rounded-lg shadow-sm p-6 mb-6">
					<Typography variant="h2" className="mb-4">
						Domains
					</Typography>

					{domains.length === 0 ? (
						<Alert variant="info">No domains configured for this tenant</Alert>
					) : (
						<div className="space-y-4">
							{domains.map((domain) => (
								<div key={domain.id} className="border rounded-lg p-4">
									<div className="flex items-center justify-between mb-2">
										<div>
											<p className="text-sm font-medium text-gray-900">
												{domain.domain}
											</p>
											<p className="text-xs text-gray-500 capitalize">
												{domain.domain_type}
											</p>
										</div>
										<div className="flex items-center gap-2">
											{getCloudFrontStatusBadge(domain.cloudfront_status)}
											{domain.domain_type === 'custom' &&
												domain.acm_certificate_status && (
													<>
														{getCertificateStatusBadge(
															domain.acm_certificate_status,
														)}
													</>
												)}
										</div>
									</div>

									{domain.domain_type === 'custom' && (
										<div className="mt-4 space-y-2">
											<div className="flex items-center gap-2">
												<Button
													onClick={() =>
														handleViewCertificateInstructions(domain.id)
													}
													variant="outline"
													size="sm"
												>
													View Certificate Instructions
												</Button>
												{domain.acm_certificate_status === 'ISSUED' && (
													<Button
														onClick={() =>
															handleCheckCertificateAndUpdate(domain.id)
														}
														variant="primary"
														size="sm"
														loading={checkingCertificate}
														icon={RefreshCw}
													>
														Check & Update CloudFront
													</Button>
												)}
											</div>

											{selectedDomainId === domain.id && certInstructions && (
												<div className="mt-4 p-4 bg-gray-50 rounded-lg">
													<Typography variant="h3" className="mb-3 text-sm">
														Certificate Validation Instructions
													</Typography>

													{certInstructions.success ? (
														<div className="space-y-4">
															<div>
																<p className="text-sm font-medium text-gray-700 mb-2">
																	{certInstructions.instructions.step1}
																</p>
																<div className="bg-white p-3 rounded border flex items-center justify-between">
																	<div className="flex-1">
																		<p className="text-xs text-gray-500">
																			CNAME Record
																		</p>
																		<p className="text-sm font-mono text-gray-900">
																			{certInstructions.validation_records.name}{' '}
																			→{' '}
																			{
																				certInstructions.validation_records
																					.value
																			}
																		</p>
																	</div>
																	<Button
																		onClick={() =>
																			copyToClipboard(
																				`${certInstructions.validation_records.name} ${certInstructions.validation_records.value}`,
																			)
																		}
																		variant="ghost"
																		size="xs"
																		icon={Copy}
																	>
																		Copy
																	</Button>
																</div>
															</div>

															<Alert variant="info">
																<p className="text-sm">
																	{certInstructions.instructions.step2}
																</p>
															</Alert>

															<Alert variant="success">
																<p className="text-sm">
																	{certInstructions.instructions.step3}
																</p>
															</Alert>

															{certInstructions.certificate_status ===
																'ISSUED' && (
																<div className="mt-4">
																	<Button
																		onClick={() =>
																			handleCheckCertificateAndUpdate(domain.id)
																		}
																		variant="primary"
																		size="sm"
																		loading={checkingCertificate}
																		icon={RefreshCw}
																		className="w-full"
																	>
																		Check Certificate & Update CloudFront
																	</Button>
																</div>
															)}
														</div>
													) : (
														<Alert variant="warning">
															{certInstructions.message ||
																'Certificate validation records not available'}
														</Alert>
													)}
												</div>
											)}

											{selectedDomainId === domain.id && certLoading && (
												<div className="mt-4 p-4 bg-gray-50 rounded-lg">
													<Loader
														size="sm"
														text="Loading certificate instructions..."
													/>
												</div>
											)}
										</div>
									)}
								</div>
							))}
						</div>
					)}
				</div>
			</PageContent>
		</div>
	);
};

export default TenantDetailsPage;
